import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Switch,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  Tabs,
} from "@mui/material";
import { Add, Search, ChatBubbleOutline, MoreVert } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import CreateChatbotModal from "./components/CreateChatbotModal";
import AppTable from "../../common/app-table";
import DashboardTabLabel from "../../common/dashboard-tab-labels";
import TableSearchInput from "../TableSearchInput";
import { formatWithTzLabel } from "../../utils/dateUtils";

const initialRows = [
  {
    id: 1,
    trigger: "Shared Inbox",
    actionType: "Auto replies",
    actionPreview: "You can use Botbridge's Shared...",
    conversationSent: 0,
    createdOn: "06/02/2026",
    updatedOn: "06/02/2026",
    status: true,
  },
  {
    id: 2,
    trigger: "WhatsApp Campaigns",
    actionType: "Auto replies",
    actionPreview: "You can use Botbridge's Shared...",
    conversationSent: 0,
    createdOn: "06/02/2026",
    updatedOn: "06/02/2026",
    status: true,
  },
  {
    id: 3,
    trigger: "Order Return Policy",
    actionType: "Auto replies",
    actionPreview: "You can use Botbridge's Shared...",
    conversationSent: 0,
    createdOn: "06/02/2026",
    updatedOn: "06/02/2026",
    status: false,
  },
  {
    id: 4,
    trigger: "Order Delivery Time",
    actionType: "Auto replies",
    actionPreview: "You can use Botbridge's Shared...",
    conversationSent: 0,
    createdOn: "06/02/2026",
    updatedOn: "06/02/2026",
    status: true,
  },
  {
    id: 5,
    trigger: "WhatsApp Automation",
    actionType: "Auto replies",
    actionPreview: "You can use Botbridge's Shared...",
    conversationSent: 0,
    createdOn: "06/02/2026",
    updatedOn: null,
    status: true,
  },
];

export default function ChatbotStartPoint() {
  const [rows, setRows] = useState(initialRows);
  const [search, setSearch] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [activeChannel, setActiveChannel] = useState("WhatsApp");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filtered = rows.filter((r) =>
    r.trigger.toLowerCase().includes(search.toLowerCase()),
  );

  const handleToggle = (id) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: !r.status } : r)),
    );
  };

  const handleMenuOpen = (e, id) => {
    setMenuAnchor(e.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRowId(null);
  };

  const handleSearchChange = (value) => {
    if (value.length > 50) return;
    setSearch(value);
    setCharCount(value.length);
  };

  const channels = [
    { label: "WhatsApp", value: "WhatsApp" },
    { label: "Instagram", value: "Instagram" },
  ];

  const handleChannelChange = (event, newValue) => {
    setActiveChannel(channels[newValue].value);
  };

  const selectedIndex = channels.findIndex(
    (tab) => tab.value === activeChannel,
  );

  const columns = [
    {
      id: "trigger",
      label: "Trigger",
      minWidth: 160,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: "#1D2330" }}>
          {row.trigger}
        </Typography>
      ),
    },
    {
      id: "actionType",
      label: "Action Type",
      minWidth: 130,
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChatBubbleOutline sx={{ fontSize: 15, color: "#6C7C93" }} />
          <Typography sx={{ ...typography.bodySmall, color: "#6C7C93" }}>
            {row.actionType}
          </Typography>
        </Box>
      ),
    },
    {
      id: "actionPreview",
      label: "Action Preview",
      minWidth: 200,
      render: (row) => (
        <Box
          sx={{
            bgcolor: "#F9F9F9",
            px: 1.5,
            py: 1,
            borderRadius: "2px",
            display: "inline-block",
          }}
        >
          <Typography
            sx={{
              ...typography.bodySmall,
              color: "#676F7E",
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.actionPreview}
          </Typography>
        </Box>
      ),
    },
    {
      id: "conversationSent",
      label: "Conversation Sent",
      minWidth: 140,
      align: "center",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#1D2330", fontWeight: 500 }}>
          {row.conversationSent}
        </Typography>
      ),
    },
    {
      id: "dates",
      label: "Created/Updated",
      minWidth: 160,
      render: (row) => (
        <Box>
          <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", lineHeight: 1.8 }}>
            Created on {row.createdOn}
          </Typography>
          {row.updatedOn && (
            <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", lineHeight: 1.8 }}>
              Updated on {formatWithTzLabel(row.updatedOn)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Switch
          checked={row.status}
          onChange={() => handleToggle(row.id)}
          sx={{
            width: 44,
            height: 24,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: "3px",
              "&.Mui-checked": {
                transform: "translateX(20px)",
                color: "#fff",
                "& + .MuiSwitch-track": {
                  backgroundColor: ColorsConstants.primaryBackgroundColor,
                  opacity: 1,
                },
              },
            },
            "& .MuiSwitch-thumb": {
              width: 18,
              height: 18,
              boxShadow: "none",
            },
            "& .MuiSwitch-track": {
              borderRadius: 13,
              backgroundColor: "#C8D0E0",
              opacity: 1,
            },
          }}
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (row) => (
        <IconButton
          size="small"
          sx={{
            color: "#6C7C93",
            borderRadius: "6px",
            "&:hover": { bgcolor: "#F0F2F8", color: "#1D2330" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, row.id);
          }}
        >
          <MoreVert sx={{ fontSize: 18 }} />
        </IconButton>
      ),
    },
  ];

  const rowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
    "&:hover": {
      backgroundColor: index % 2 === 0 ? "#E6F0FF" : "#F7F9FC",
    },
  });

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA", p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ ...typography.h1, color: "#1D2330" }}>
            Chatbot Start Point
          </Typography>
          <Typography
            sx={{
              ...typography.bodySmall,
              color: "#6C7C93",
              mt: 0.25,
            }}
          >
            Auto-reply to specific questions
          </Typography>
        </Box>
        <ButtonOne
          variant="contained"
          startIcon={<Add />}
          title="Add New Custom Reply"
          sx={{ backgroundColor: ColorsConstants.primaryBlue100 }}
          onClick={() => setModalOpen(true)}
        >
          Add New Custom Reply
        </ButtonOne>
      </Box>

      {/* Search + Channel Tabs */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={search}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={charCount}
            tooltip="Search by Trigger"
            placeholder="Search by Trigger"
          />
        </Box>

        <Box sx={{ width: "225px" }}>
          <Tabs
            value={selectedIndex === -1 ? 0 : selectedIndex}
            onChange={handleChannelChange}
            sx={{
              minHeight: 53,
              height: "53px",
              p: "4px",
              borderRadius: "10px",
              backgroundColor: "#F3F5F7",
              "& .MuiTabs-flexContainer": {
                justifyContent: "space-between",
                height: "100%",
              },
              "& .MuiTabs-indicator": { display: "none" },
            }}
          >
            {channels.map((tab, index) => (
              <DashboardTabLabel
                key={index}
                tab={tab.label}
                value={index}
                selectedIndex={selectedIndex}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Table using AppTable */}
      <AppTable
        columns={columns}
        rows={filtered}
        rowKey="id"
        maxHeight={500}
        containerSx={{ minWidth: 1200 }}
        rowSx={rowSx}
        paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
        emptyMessage="No triggers found."
      />

      {/* Row Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            border: "1px solid #DFE5EC",
            borderRadius: "8px",
            minWidth: 140,
            "& .MuiMenuItem-root": {
              ...typography.bodySmall,
              color: "#1D2330",
              py: 1,
              px: 2,
              "&:hover": { bgcolor: "#F0F4FA" },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleMenuClose}>Edit</MenuItem>
        <MenuItem onClick={handleMenuClose}>Duplicate</MenuItem>
        <MenuItem
          onClick={handleMenuClose}
          sx={{ color: "#E05252 !important" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create Modal */}
      <CreateChatbotModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
}